import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

/**
 * Small (i) icon that reveals a definition on hover.
 * The tooltip is rendered into a body-level portal with fixed positioning,
 * so it escapes any `overflow:hidden` / `truncate` / scroll-container clipping
 * (cards, table wrappers) that a plain absolute tooltip would get trapped by.
 */
export const InfoTip: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const show = () => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Tooltip is w-56 (224px) and centered on the icon; clamp so it never
    // overflows the viewport edges (e.g. the rightmost Δ column).
    const half = 112;
    const margin = 8;
    const center = r.left + r.width / 2;
    const left = Math.max(half + margin, Math.min(center, window.innerWidth - half - margin));
    setPos({ top: r.top, left });
  };
  const hide = () => setPos(null);

  return (
    <span
      ref={ref}
      className={`relative inline-flex align-middle ${className || ''}`}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-help" />
      {pos && createPortal(
        <span
          role="tooltip"
          style={{ position: 'fixed', top: pos.top - 8, left: pos.left, transform: 'translate(-50%, -100%)' }}
          className="pointer-events-none z-[9999] w-56 whitespace-normal rounded-lg bg-gray-900 px-3 py-2
                     text-left text-xs font-normal leading-snug text-white shadow-lg"
        >
          {text}
        </span>,
        document.body,
      )}
    </span>
  );
};

/** Collapsible "How to read this report" definitions block for the bottom of a report. */
export const ReportNotes: React.FC<{ items: Array<{ term: string; def: string }> }> = ({ items }) => (
  <details className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 group">
    <summary className="flex items-center gap-2 cursor-pointer select-none px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">
      <Info className="h-4 w-4 text-brandColor" />
      How to read this report
      <span className="ml-auto text-xs font-normal text-gray-400 group-open:hidden">click to expand</span>
    </summary>
    <dl className="px-4 pb-4 pt-1 grid sm:grid-cols-2 gap-x-8 gap-y-3">
      {items.map((it) => (
        <div key={it.term}>
          <dt className="text-xs font-semibold text-gray-700 dark:text-gray-200">{it.term}</dt>
          <dd className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{it.def}</dd>
        </div>
      ))}
    </dl>
  </details>
);

export default InfoTip;
